import { ICategory } from '@/models/category.model';
import { Permissions } from '@/models/permissions.model';
import { IUser } from '@/models/user.model';
import { MessageActionName } from '@/types/message/action.type';
import { MessageStateGermanTextType, MessageStateType } from '@/types/message/state.type';

// the message object we fetch due to `fields:...`
export interface Message {
  id: number;
  text: string;
  created: string;
  message_type: ICategory;
  _permissions?: Permissions;
  responsible: {
    avatar_uri: string;
    email: string;
    id: number;
    internal_name: string;
    internal_name_long: string;
    public_name: string;
    virtual: number;
  };
  private?: number;
  state_german: MessageStateGermanTextType;
  lat: number;
  lon: number;
  search_string: string;
  address: string;
  related_geometry_names: string[];
  owner: IUser;
  state: MessageStateType;
  remind_on: string;
  attribute_values: AttributeValues[];
  archived?: any;
  escalate_on: string;
  priority?: string | null;
  marker_uri: string;
  thumbnail_sq64: string;
  title?: string;
  attachments: Attachment[];
  history: History[];
}
export interface History {
  manual_text: string;
  text: string;
  created: string;
  owner: {
    avatar_uri: string;
    public_name: string;
    internal_name: string;
    links: {
      self: {
        href: string;
      };
    };
    id: number;
    internal_name_long: string;
    email: string;
    virtual: number;
  };
  id: number;
}

export interface ExtendedMessage {
  id: number;
  name: string;
  text: string;
  title: string;
  created: string;
  message_type: ICategory;
  _permissions?: Permissions;
  responsible?: any;
  private?: number;
  state_german: string;
  thumbnail_sq64: string;
  lat: number;
  lon: number;
  marker_uri: string;
  search_string: string;
  address: string;
  related_geometry_names: string[];
  owner: IUser;
  state: string;
  escalate_on: string;
  remind_on: string;
  attachments: Attachment[];
  attribute_values: AttributeValues[];
  archived?: any;
  priority?: string | null;
}

export interface MessagePut {
  id?: number;
  name?: string;
  message_type?: ICategory;
  _permissions?: {
    _permissions: {
      update: true;
      view: true;
      actions: { set_all_updates_read: true };
      delete: false;
    };
  };
  responsible?: any;
  private?: number;
  attribute_values?: any;
  message?: { id: number; text: string; title: string | null };
  text?: string;
}

export interface AttributeValues {
  id: number;
  name: string;
  code: string;
  values: string[];
  values_human: string[];
}

export interface Action {
  class?: string | null;
  value?: string | null;
  buttonText?: string | null;
  id?: number | undefined;
  autocomment: string;
  candelegate: number;
  comment_required: number;
  default: number;
  default_template_id: number;
  disable_delegate_external: number;
  has_modify_escalate: number;
  has_remindon: number;
  name: string;
  name_en: MessageActionName;
  responsibleid: number;
  text: string;
  icon?: string;
  isStateInMessageHistory?: boolean;
  index?: number;
  position?: number;
}

export interface Attachment {
  content_type: string;
  id: number;
  public: number;
  thumbnails: ThumbnailsSize;
  title: string;
  type: string;
  url: string;
  index?: number;
  message_update?: { created: string; id: number; type: string };
  parent: string;
  created: string;
  tag: string;
  iconClass?: string;
  class?: string;
  filename?: string;
  attachmentType?: string;
  size?: number;
}
export interface ThumbnailsSize {
  sq256: string;
  sq128: string;
  res400: string;
  res800: string;
  w256: string;
}

export interface MessageHistory {
  _permissions: Permissions;
  solved: string;
  text: string;
  id: number;
  owner: IUser;
  picture_url: string;
  visible: 1 | 0;
  message_update: MessageUpdate;
  action: MessageActionName;
  manual_text: string;
  updateid: number;
  created: string;
  data: {
    owner_communication: boolean | undefined;
    system_user: number | undefined;
  };
  icon_url?: string;
  messageid?: number;
  ming_category?: 'public' | 'internal' | 'protocol' | 'external';
}

interface MessageUpdate {
  attachments: Array<Attachment>;
  attribute_values: Array<any>;
  id: number;
  text: string;
}

export const messageActionMappedToGerman = {
  create: 'Warten auf Freigabe',
  approve: 'In Bearbeitung',
  check: 'In Bearbeitung',
  reject: 'Abgelehnt',
  solve: 'Gelöst Abgeschlossen',
  unsolvable: 'ungelöst Abgeschlossen',
  reopen: 'Wiedereröffnet',
};
export interface ExportPDF {
  include_contact?: number | boolean;
  include_history?: number | boolean;
  lat?: number;
  lon?: number;
  map_height?: number;
  map_width?: number;
  zoom?: number;
  configurable?: number;
}
export interface UploadFile {
  objectURL: string;
  lastModified: number;
  name: string;
  size: number;
  type: string;
  id: number;
  visible?: number;
}

export interface ModifyImage {
  blur?: {
    areas: ModifyArea[];
    type: boolean;
  };
  rotate?: { rotateGrad?: number };
  crop?: ModifyArea;
}
export interface ModifyArea {
  x?: number;
  width?: number;
  y?: number;
  height?: number;
}

export interface PriorityAttribute {
  id: string;
  metadata: string;
  ordering: number;
  text: string;
}

export interface DefaultResponsibleRequestParams {
  new_type?: number;
  new_lat?: number;
  new_lon?: number;
  new_state?: any;
  new_typeid?: number;
}

export interface UploadFileBody {
  link_title?: string;
  link_url?: string;
  visible?: number;
}

export const messageStateMappedToAction = {
  created: 'create',
  approved: 'approve',
  checked: 'check',
  rejected: 'reject',
  solved: 'solve',
  unsolvable: 'unsolvable',
  special: 'special',
} as const;

export const messageActionMappedToState = {
  create: 'created',
  approve: 'approved',
  check: 'checked',
  reject: 'rejected',
  solve: 'solved',
  unsolvable: 'unsolvable',
  special: 'special',
} as const;
import { ICategoryShort } from './category.model';
import { IUserShort } from './user.model';

export interface IBmsMessage {
  thumbnail_sq64: string;
  number: number;
  id: number;
  attribute_values: Array<IAttributes>;
  created: string;
  text: string;
  marker_uri: string;
  links: IMessageLinks;
  state: string;
  updates: any[];
  responsible: IUserShort;
  thumbnail_sq128: string;
  title: string | null;
  state_german: string;
  message_type: ICategoryShort;
}

export interface IAttributes {
  name: string;
  id: number;
  code: string;
  values: Array<any>;
  values_human: Array<any>;
}

export interface IMessageLinks {
  message_type: {
    href: string;
  };
  responsible: {
    href: string;
  };
  self: {
    href: string;
  };
}
